import clsx from 'clsx';
import NextLink from 'next/link';
import { useMemo } from 'react';
import { sanitizeStringForUrl } from '~/features/navigation/components/search/search-utils';
import { usePDPData } from '~/features/product-details/context';
import { Typography, TypographyProps } from '~/shared/components';
import { useFrame } from '~/shared/utils';
import styles from './ProductBrand.module.scss';

type Props = Omit<TypographyProps, 'children'>;

/**
 * The brand doubles as a way into its own page, so it links when one can be built for it and
 * reads as plain text when it cannot.
 */
export const ProductBrand: React.FC<Props> = ({
    className,
    variant = 'paragraph-M',
    weight = 'medium',
    ...rest
}) => {
    const { data: frame } = useFrame();
    const { product } = usePDPData();

    const brandLink = useMemo(() => {
        const brandsOverviewPageUrl = frame?.settings?.pageReferences?.brandsOverviewPage?.url;

        if (!product.brand || !brandsOverviewPageUrl) return undefined;

        const sanitizedBrandName = sanitizeStringForUrl(product.brand);

        return sanitizedBrandName ? `/brands/${sanitizedBrandName}` : undefined;
    }, [product.brand]);

    if (!product.brand) return null;

    return (
        <Typography
            className={clsx(styles.productBrand, className)}
            variant={variant}
            weight={weight}
            {...rest}
        >
            {brandLink ? (
                // NextLink leaves modifier clicks to the browser, so cmd/ctrl-click still
                // opens the brand page in a new tab.
                <NextLink className={styles.productBrandLink} href={brandLink}>
                    {product.brand}
                </NextLink>
            ) : (
                product.brand
            )}
        </Typography>
    );
};
